<template>
  <section
    class="relative overflow-hidden rounded-base bg-black px-5 py-7 text-white sm:px-8 sm:py-9 lg:px-12 lg:py-11"
    data-test="guide-profile-hero"
  >
    <div
      class="pointer-events-none absolute -top-32 -right-24 h-80 w-80 rounded-full bg-gold/20 blur-3xl"
      aria-hidden="true"
    ></div>
    <div
      class="relative grid items-center gap-7 lg:grid-cols-[auto_1fr] lg:gap-10"
    >
      <figure class="mx-auto w-full max-w-56 lg:mx-0 lg:w-56">
        <NuxtImg
          v-if="guide.image"
          :src="imageSource"
          :alt="imageAlt"
          class="aspect-square w-full rounded-base border border-white/15 bg-light-200 object-cover shadow-2xl"
          format="webp"
          width="448"
          height="448"
          sizes="224px"
          quality="85"
          loading="eager"
          fetchpriority="high"
        />
        <div
          v-else
          class="flex aspect-square w-full items-center justify-center rounded-base border border-white/15 bg-white/10"
          data-test="guide-profile-image-placeholder"
        >
          <IconWalkative class="w-3/4 max-w-36 fill-white/70" />
        </div>
        <figcaption
          v-if="guide.image_caption"
          class="mt-2 text-center text-xs leading-relaxed text-white/70 lg:text-left"
        >
          {{ guide.image_caption }}
        </figcaption>
      </figure>

      <div class="min-w-0 text-center lg:text-left">
        <h1 class="text-3xl text-white sm:text-4xl lg:text-5xl">
          {{ guide.first_name }}
        </h1>

        <div
          class="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-sm text-white/85 lg:justify-start"
        >
          <div v-if="guide.city" class="flex items-center gap-2">
            <Icon
              icon="marker-pin"
              class="text-base text-gold"
              aria-hidden="true"
            />
            <span class="font-medium">{{ guide.city }}</span>
          </div>
          <div v-if="guide.languages.length" class="flex items-center gap-2">
            <span class="text-white/60">{{ $t("pages.guide.languages") }}</span>
            <LanguageFlags
              class="!ml-0 [&>img:first-child]:!ml-0"
              :languages="guide.languages"
              :max-flags="10"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { Guide } from "@/types";
import { getNormalizedImageUrl } from "@/utils/getNormalizedImageUrl";

const props = defineProps<{
  guide: Guide;
}>();

const config = useRuntimeConfig();
const imageSource = computed(() =>
  getNormalizedImageUrl(
    props.guide.image,
    config.public.backendBaseUrl,
  ),
);
const imageAlt = computed(
  () => props.guide.image_alt?.trim() || props.guide.first_name,
);
</script>
